import React from 'react';
import { ApptRow } from './ApptRow';
import { formatDayHeading } from './date';
import { isPastAppointment, needsChartNote } from './status';
import type { CalendarAppointment } from './types';

export interface DayGroupProps {
    date: string;
    appointments: CalendarAppointment[];
    todayIso: string;
    readOnly: boolean;
    showPractice?: boolean;
    renderPractice?: (appointment: CalendarAppointment) => React.ReactNode;
    renderAvatar: (appointment: CalendarAppointment) => React.ReactNode;
    renderStage?: (appointment: CalendarAppointment) => React.ReactNode;
    onSelect: (appointment: CalendarAppointment) => void;
    onCancel?: (appointment: CalendarAppointment) => void;
    onAddNote?: (appointment: CalendarAppointment) => void;
}

export const DayGroup: React.FC<DayGroupProps> = ({
    date,
    appointments,
    todayIso,
    readOnly,
    showPractice,
    renderPractice,
    renderAvatar,
    renderStage,
    onSelect,
    onCancel,
    onAddNote
}) => {
    // Every row shares this group's `date` and canceled ones are filtered
    // upstream, so a direct date comparison equals isPastAppointment here.
    const isPast = date < todayIso;

    return (
        <div>
            <div className="sticky top-0 z-10 flex items-baseline gap-2 px-5 py-2 bg-gray-50/95 backdrop-blur border-b border-gray-200">
                <span
                    className={`text-[12px] font-semibold ${date === todayIso ? 'text-gray-900' : 'text-gray-600'}`}
                >
                    {formatDayHeading(date, todayIso)}
                </span>
                <span className="text-[11px] text-gray-400">{appointments.length}</span>
                {isPast && (
                    <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-[10.5px] font-medium">
                        Past
                    </span>
                )}
            </div>
            <div className="bg-white">
                {appointments.map((appointment) => (
                    <ApptRow
                        key={appointment.id}
                        appointment={appointment}
                        isPast={isPastAppointment(appointment, todayIso)}
                        needsNote={needsChartNote(appointment, todayIso)}
                        readOnly={readOnly}
                        showPractice={showPractice}
                        renderPractice={renderPractice}
                        renderAvatar={renderAvatar}
                        renderStage={renderStage}
                        onSelect={onSelect}
                        onCancel={onCancel}
                        onAddNote={onAddNote}
                    />
                ))}
            </div>
        </div>
    );
};

export default DayGroup;
